import { db, storage } from '@/data/firebase/config';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { Gallery, GalleryImage } from '@/domain/types/collections';
import { useAdminLogger } from '@/domain/use-cases/admin-logger';
import GalleryImagesUpload from '@/presentation/components/gallery-images-upload';
import Spinner from '@/presentation/components/spinner';
import {
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { useEffect, useState } from 'react';
import { FiArrowLeft, FiSave, FiTrash2 } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const GalleryDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { logUpdate, logDelete } = useAdminLogger();

  const [gallery, setGallery] = useState<Gallery | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [formData, setFormData] = useState<Omit<Gallery, 'id' | 'createdAt'>>({
    title: '',
    titleEN: '',
    images: [],
  });

  useEffect(() => {
    const fetchGallery = async () => {
      if (!id) return;

      try {
        const docRef = doc(db, DOCUMENTS_DB.GALLERIES, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = { id: docSnap.id, ...docSnap.data() } as Gallery;
          setGallery(data);
          setFormData({
            title: data.title || '',
            titleEN: data.titleEN || '',
            images: data.images || [],
          });
        } else {
          toast.error('Galería no encontrada', { theme: 'colored' });
          navigate('/dashboard/galleries');
        }
      } catch (error) {
        console.error('Error fetching gallery:', error);
        toast.error('Error al cargar la galería', { theme: 'colored' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchGallery();
  }, [id, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImagesChange = (images: GalleryImage[]) => {
    setFormData((prev) => ({ ...prev, images }));
  };

  const handleUpdate = async () => {
    if (!id) return;

    if (!formData.title.trim()) {
      toast.error('El título en español es requerido', { theme: 'colored' });
      return;
    }

    if (!formData.titleEN.trim()) {
      toast.error('El título en inglés es requerido', { theme: 'colored' });
      return;
    }

    setIsSubmitting(true);

    try {
      const docRef = doc(db, DOCUMENTS_DB.GALLERIES, id);
      await updateDoc(docRef, {
        ...formData,
        updatedAt: serverTimestamp(),
      });

      // Log the action
      await logUpdate(DOCUMENTS_DB.GALLERIES, id, formData.title, formData);

      toast.success('Galería actualizada correctamente', { theme: 'colored' });
    } catch (error) {
      console.error('Error updating gallery:', error);
      toast.error('Error al actualizar la galería', { theme: 'colored' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !gallery) return;

    setIsSubmitting(true);

    try {
      // Eliminar todas las imágenes del storage
      if (gallery.images && gallery.images.length > 0) {
        toast.info('Eliminando imágenes del storage...');
        for (const image of gallery.images) {
          if (image.url && image.url.includes('firebase')) {
            try {
              const imageRef = ref(storage, image.url);
              await deleteObject(imageRef);
            } catch (error) {
              console.warn('No se pudo eliminar la imagen:', image.url, error);
            }
          }
        }
      }

      // Eliminar el documento de Firestore
      const docRef = doc(db, DOCUMENTS_DB.GALLERIES, id);
      await deleteDoc(docRef);

      // Log the action
      await logDelete(DOCUMENTS_DB.GALLERIES, id, gallery.title);

      toast.success('Galería eliminada correctamente', { theme: 'colored' });
      navigate('/dashboard/galleries');
    } catch (error) {
      console.error('Error deleting gallery:', error);
      toast.error('Error al eliminar la galería', { theme: 'colored' });
    } finally {
      setIsSubmitting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Galería no encontrada
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col gap-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard/galleries')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiArrowLeft size={24} />
          </button>
          <h1 className="text-3xl font-bold">Editar Galería</h1>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isSubmitting}
            className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FiTrash2 />
            Eliminar
          </button>

          <button
            onClick={handleUpdate}
            disabled={isSubmitting}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FiSave />
            {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Confirmar Eliminación</h2>
            <p className="text-gray-600 mb-6">
              ¿Está seguro de que desea eliminar la galería "{gallery.title}"?
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={isSubmitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Títulos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título (Español) *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Ingrese el título en español"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title (English) *
            </label>
            <input
              type="text"
              name="titleEN"
              value={formData.titleEN}
              onChange={handleInputChange}
              placeholder="Enter title in English"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Información adicional */}
        {gallery.createdAt && (
          <div className="text-sm text-gray-500">
            Creada el:{' '}
            {new Date(gallery.createdAt.seconds! * 1000).toLocaleString(
              'es-ES',
            )}
          </div>
        )}

        {/* Imágenes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Imágenes de la Galería ({formData.images.length} imagen
            {formData.images.length !== 1 ? 'es' : ''})
          </label>
          <GalleryImagesUpload
            images={formData.images}
            onChange={handleImagesChange}
            galleryId={id}
            collectionName={DOCUMENTS_DB.GALLERIES}
          />
        </div>
      </div>
    </div>
  );
};

export default GalleryDetailsPage;
